// inventory.js — pulls live stock + variant IDs from the public Shopify
// products feed. No auth required; Shopify ships /products.json with
// CORS-permissive headers on every storefront.
//
// We map each Shopify product's `handle` to its first retail variant. Every
// simulator product's `id` matches a Shopify `handle`, so the merge in
// products.js is a straight Map.get(p.id) lookup.

const DEFAULT_ORIGIN = 'https://retrobakery.net';
const DEFAULT_INTERVAL_MS = 5 * 60 * 1000;

// Returns: Map<handle, { variantId: number, available: boolean, price: number }>
// On failure: returns null. Caller decides fallback behavior (we keep prior
// state in main.js so a transient network blip doesn't suddenly mark
// everything sold out).
export async function fetchInventoryMap(origin = DEFAULT_ORIGIN) {
  try {
    const r = await fetch(`${origin}/products.json?limit=250`, { cache: 'no-store' });
    if (!r.ok) return null;
    const { products } = await r.json();
    const map = new Map();
    for (const p of products) {
      const v = p.variants && p.variants[0];
      if (!v) continue;
      map.set(p.handle, {
        variantId: Number(v.id),
        available: Boolean(v.available),
        price: Number(v.price),
      });
    }
    return map;
  } catch (e) {
    console.warn('[inventory] fetch failed:', e);
    return null;
  }
}

// Calls onUpdate(inventoryMap) every `intervalMs` while the tab is visible,
// and once immediately whenever the tab regains visibility. Returns a stop
// function. Skips overlapping fetches so a slow network can't pile up
// requests.
export function startInventoryPolling(onUpdate, intervalMs = DEFAULT_INTERVAL_MS) {
  let inFlight = false;
  const tick = async () => {
    if (inFlight) return;
    if (document.visibilityState !== 'visible') return;
    inFlight = true;
    try {
      const inv = await fetchInventoryMap();
      if (inv) onUpdate(inv);
    } finally {
      inFlight = false;
    }
  };
  const timer = setInterval(tick, intervalMs);
  const onVis = () => { if (document.visibilityState === 'visible') tick(); };
  document.addEventListener('visibilitychange', onVis);
  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVis);
  };
}
